import React, { useState, useEffect, useRef } from "react";
import { Handle, Position } from "reactflow";
import './CustomNode.css';

const getPlaceholderText = (tag) => {
  const placeholders = {
    "主張": "主張を入力してください...",
    "根拠": "根拠を入力してください...",
    "具体例": "具体例を入力してください...",
    "結論": "結論を入力してください...",
  };
  return placeholders[tag] || "内容を入力してください...";
};

const FlawTooltip = ({ message }) => {
  if (!message) return null;
  const style = {
    position: 'absolute',
    top: '105%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    zIndex: 100,
    whiteSpace: 'nowrap',
  };
  return <div style={style}>{message}</div>;
};

const CustomNode = ({ data, selected }) => {
  const { label, tag, onUpdateNode, id, isIsolated, isCycled, isShadowHidden, isDragging, resetHover } = data;
  const [editValue, setEditValue] = useState(label);
  const [selectedTag, setSelectedTag] = useState(tag);
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setEditValue(label);
  }, [label]);

  useEffect(() => {
    setSelectedTag(tag);
  }, [tag]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [editValue]);

  useEffect(() => {
    if (resetHover) {
      setIsHovered(false);
      onUpdateNode(id, { resetHover: false });
    }
  }, [resetHover, onUpdateNode, id]);

  useEffect(() => {
    if (isActive && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isActive]);

  const handleTextareaChange = (e) => {
    setEditValue(e.target.value);
    if (onUpdateNode) {
        onUpdateNode(id, { label: e.target.value, tag: selectedTag });
    }
  };

  const handleTextareaBlur = () => {
    setIsActive(false);
    if (onUpdateNode && editValue !== label) {
      onUpdateNode(id, { label: editValue, tag: selectedTag });
    }
  };

  const handleTextareaClick = () => {
    setIsActive(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.stopPropagation();
    } else if (e.key === "Enter") {
        // e.preventDefault();
        // setIsActive(false);
        // e.currentTarget.blur();
    } else if (e.key === "Escape") {
        setIsActive(false);
        e.currentTarget.blur();
    }
  };

  const handleTagChange = (e) => {
    const newTag = e.target.value;
    setSelectedTag(newTag);
    if (onUpdateNode) {
      onUpdateNode(id, { label: editValue, tag: newTag });
    }
  };

  const getTagColor = (tag) => {
    const colors = {
      "主張": "#f28482",
      "根拠": "#84a9f4",
      "具体例": "#f6bd60",
      "結論": "#90be6d"
    };
    return colors[tag] || "#333";
  };

  const getBorderColor = () => {
    if (isIsolated) return '#ffa500';
    if (isCycled) return '#ff0072';
    if (selected) return '#007bff';
    return '#333';
  };

  const nodeStyle = {
    position: "relative",
    padding: "28px 14px 14px",
    border: `2px solid ${getBorderColor()}`,
    borderRadius: "6px",
    backgroundColor: "#fff",
    width: "260px",
    wordWrap: "break-word",
    lineHeight: "1.6",
    boxShadow: isShadowHidden ? 'none' : (isIsolated || isCycled
      ? `0 0 12px ${getBorderColor()}`
      : (selected ? "3px 3px 12px rgba(0,123,255,0.3)" : "3px 3px 12px rgba(0,0,0,0.15)")),
  };

  const textareaStyle = {
    minHeight: '80px',
    boxSizing: "border-box",
    width: "100%",
    padding: "2px",
    fontSize: "14px",
    fontFamily: "inherit",
    lineHeight: "1.6",
    resize: "none",
    outline: "none",
    border: isActive ? "1px solid #ddd" : "1px solid transparent",
    backgroundColor: isActive ? "#fff" : "transparent",
    overflow: "hidden",
    caretColor: isActive ? "#000" : "transparent",
    cursor: "text"
  };

  const getFlawMessage = () => {
    if (isIsolated) return "このノードは他のノードから孤立しています。";
    if (isCycled) return "このノードは循環参照の一部です。";
    return null;
  };

  return (
    <div
      style={nodeStyle}
      className={(isCycled) ? 'node-pulse' : ''}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={(e) => {
        if (e.buttons === 0) {
          setIsHovered(false);
        }
      }}
    >
      {isHovered && !isDragging && <FlawTooltip message={getFlawMessage()} />}
      <div
        style={{
          position: "absolute",
          top: "-16px",
          left: "12px",
          background: "#fff",
          border: `2px solid ${getBorderColor()}`,
          borderRadius: "4px",
          padding: "3px 8px",
          fontSize: "12px",
          fontWeight: "bold",
          boxShadow: "2px 2px 4px rgba(0,0,0,0.1)",
          transition: "border-color 0.2s ease",
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="nodrag"
      >
        <select
          value={selectedTag}
          onChange={handleTagChange}
          style={{
            border: "none",
            background: "transparent",
            fontSize: "12px",
            fontWeight: "bold",
            color: getTagColor(selectedTag),
            cursor: "pointer",
            outline: "none",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="主張">主張</option>
          <option value="根拠">根拠</option>
          <option value="具体例">具体例</option>
          <option value="結論">結論</option>
        </select>
      </div>

      <div className="nodrag" style={{ fontSize: "14px", minHeight: "20px", marginTop: '-8px' }}>
      <textarea
        ref={textareaRef}
        value={editValue || ""}
        onChange={handleTextareaChange}
        onBlur={handleTextareaBlur}
        onClick={handleTextareaClick}
        onKeyDown={handleKeyDown}
        readOnly={!isActive}
        placeholder={getPlaceholderText(selectedTag)}
        style={textareaStyle}
        title="クリックで編集"
      />
      </div>

      <Handle type="target" position={Position.Left} style={{ left: -10, width: 12, height: 12, background: '#007bff' }} />
      <Handle type="source" position={Position.Right} style={{ right: -10, width: 12, height: 12, background: '#007bff' }} />
    </div>
  );
};

export default CustomNode;
